import * as icons from '../../../assets/icons'
import { BigImageFormFields } from './types'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { FileUploadInput } from '../../util/SettingsUtil'
import { FlowpageProps } from '../types/FlowpageProps'
import { FormFields } from '../types'
import { Stack, Typography } from '@dtx-company/design-system/src'
import { UseFormMethods } from 'react-hook-form-deprecated'
import { Yield } from '@dtx-company/shared-components/src/foundation/Icons/Bespoke/Yield'
import { exceedsMaxFileSize } from '@dtx-company/shared-components/src/components/organisms/ImageEdit/utils'
import { getBigImageStyle } from './utils'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useLinkImageEditModalState } from '../components/LinkImageEditModal/hooks'
import { usePreviousValue } from '@dtx-company/true-common/src/hooks/usePreviousValue/usePreviousValue'
import Image from 'next/image'

/**
 * Component that renders the actual image in the flowpage, preview and manager
 * for the Big Image section
 */
export const BigImageComponent: FC<FlowpageProps> = ({
  link,
  isEditLinkPreview
}: FlowpageProps) => {
  const { actionData, thumbNailImgUrl } = link
  const { borderRadius, width, height } = getBigImageStyle(actionData?.maskType)

  return useMemo(
    () => (
      <Stack justifyContent="center">
        <Image
          src={thumbNailImgUrl as string}
          width={200} // initial resolution
          height={200}
          style={{
            width: isEditLinkPreview ? width : '100%',
            height: isEditLinkPreview ? height : 'initial',
            borderRadius
          }}
          alt=""
          objectFit="contain"
        />
      </Stack>
    ),
    [borderRadius, height, isEditLinkPreview, thumbNailImgUrl, width]
  )
}

interface BigImagePickerAndEditorProps {
  id: string
  imageUrl?: string
  replaceButtonCTA?: string
  deleteButtonCTA?: string
  setValue: UseFormMethods['setValue']
  imageFormFieldName?: string
  maskTypeFormFieldName?: string
  showImageSizeWarningToast?: boolean
  setImageSizeWarning?: (val: boolean) => void
  addCopy?: string
  deleteImage?: boolean
}

export const BigImagePickerAndEditor: FC<BigImagePickerAndEditorProps> = ({
  imageUrl,
  setValue,
  addCopy = 'Add Image',
  imageFormFieldName = FormFields.IMAGE,
  maskTypeFormFieldName = BigImageFormFields.maskType
}) => {
  const { resultImageUrl, maskType, openImageEditModal } = useLinkImageEditModalState()

  const handleStagingImage = useCallback(
    (e: File | Blob): void => {
      const stagedImage = e

      if (stagedImage) {
        if (exceedsMaxFileSize(stagedImage.size)) {
          sendErrorNotification('Image is too large to upload (size limit 10MB)')
        } else {
          openImageEditModal({
            sourceImageUrl: URL.createObjectURL(stagedImage),
            maskType
          })
        }
      }
    },
    [maskType, openImageEditModal]
  )

  const prevMaskType = usePreviousValue(maskType)
  const prevImageUrl = usePreviousValue(imageUrl)

  useEffect(() => {
    if (resultImageUrl && resultImageUrl !== prevImageUrl) {
      setValue(imageFormFieldName, resultImageUrl)
    }
    if (maskType && maskType !== prevMaskType) {
      setValue(maskTypeFormFieldName, maskType)
    }
  }, [
    imageFormFieldName,
    maskType,
    maskTypeFormFieldName,
    prevImageUrl,
    prevMaskType,
    resultImageUrl,
    setValue
  ])

  const { width, height, borderRadius } = getBigImageStyle(maskType)

  const rend = useMemo(() => {
    return (
      <Stack justifyContent="center" alignItems="center">
        {imageUrl ? (
          <Image
            src={imageUrl}
            width={100}
            height={100}
            style={{
              width,
              height,
              borderRadius
            }}
            alt=""
          />
        ) : (
          <Stack
            sx={{
              width,
              height,
              borderRadius,
              backgroundColor: 'grey'
            }}
            direction="column"
            justifyContent="center"
            alignItems="center"
            gap="8px"
          >
            <Image src={icons.photoOutlinedWhite} width={25} height={25} alt="" />

            <Typography variant="labelSmall" color="white">
              {addCopy}
            </Typography>
          </Stack>
        )}
      </Stack>
    )
  }, [addCopy, borderRadius, height, imageUrl, width])

  return (
    <Stack gap="8px" direction="row" justifyContent="space-between">
      <FileUploadInput handleUpload={handleStagingImage} />

      {rend}
    </Stack>
  )
}

export const ImageSizeWarning: FC = () => {
  return (
    <Stack alignItems="center">
      <Yield height="60px" width="60px" color="#e1cb00" />
      <Typography>
        Warning: This photo is low resolution and may come out pixelated on larger devices
      </Typography>
    </Stack>
  )
}
